import { Link, useLocation, useParams, useSearch } from '@tanstack/react-router';
import { Note } from '../../../types/api';
import { formatRelativeTime } from '../../../utils/dateUtils';
import { getReadRoute } from '../../../hooks/useReadUrl';
import NoteDrawable from '../../Note/NoteDrawable';

interface BookmarkNotesListProps {
  notes: Note[];
  bookmarkId: string;
  /** Dismisses the containing popover after a note is opened. */
  closePopover?: () => void;
}

function BookmarkNotesList({ notes, bookmarkId, closePopover }: BookmarkNotesListProps) {
  const { pathname } = useLocation();
  const routeParams = useParams({ strict: false });
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  // Build a section-aware read URL so opening a note keeps the current section
  // (archive, liked, folder, tag, …) instead of falling back to home. Section
  // read routes only carry the bookmark id in the path, so the highlight rides
  // along as a `?highlight=` search param (see BookmarkPageContext).
  const { toPattern, baseParams } = getReadRoute(pathname, routeParams);

  return (
    <div className="bookmark-notes-list">
      {notes.map((note) => {
        const highlightId = String(note.highlight_id ?? note.id);
        const noteSearch: Record<string, unknown> = { ...search, highlight: highlightId };
        return (
          <Link
            key={note.id}
            to={toPattern}
            params={{ ...baseParams, id: note.bookmark_id || bookmarkId }}
            search={noteSearch}
            className="bookmark-note-item"
            onClick={(event) => {
              event.stopPropagation();
              closePopover?.();
            }}
          >
            <div className="bookmark-note-text note-item-quote">{note.text}</div>
            {note.note && (
              <div className="bookmark-note-comment">
                <div className="bookmark-note-comment-text">{note.note}</div>
              </div>
            )}
            <NoteDrawable note={note} />
            <span className="bookmark-note-date">{formatRelativeTime(note.time)}</span>
          </Link>
        );
      })}
    </div>
  );
}

export default BookmarkNotesList;
